import { Link } from "react-router-dom";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { SortableFieldRow } from "./SortableFieldRow";
import { SortableCreatableOptionsEditor } from "./SelectOptionsEditor";
import { useFormBuilder } from "./useFormBuilder";
import { useFieldDnD } from "./dnd";
import { field, fieldType, FieldType, SelectOption } from "../../types";
import { RuleEditor } from "./RuleEditor";
import Select from "react-select";
import { AsyncFormSelect } from "../../records/form/AsyncFormSelect";
export default function FormBuilder() {
  const fb = useFormBuilder();
  const { sensors, handleDragEnd } = useFieldDnD(fb.setFields);
  return (
    <form ref = {fb.formRef} className = "container py-3">
    <h3>{fb.isEdit ? "Edit Form" : "Create Form"}</h3>
    <div className = "mb-3">
        <label children = "Form Name" htmlFor = "form_name"/>
        <input
            id = "form_name"
            name = "form_name"
            required
            className = "form-control"
            value = {fb.formName}
            onChange = {e => fb.setFormName(e.target.value)}
        />
    </div>
    <div className = "mb-3">
        <label children = "Form Type" htmlFor = "form_type"/>
        <input
            id = "form_type"
            name = "form_type"
            className = "form-control"
            value = {fb.formType}
            onChange = {e => fb.setFormType(e.target.value)}
        />
    </div>
    <hr/>
    <h5>Fields</h5>
    <DndContext
        sensors = {sensors}
        collisionDetection = {closestCenter}
        onDragEnd = {handleDragEnd}
    >
        <SortableContext
            items = {fb.fields.map(f => f.field_key)}
            strategy = {verticalListSortingStrategy}
        >
        {fb.fields.map((field, index) => (
            <SortableFieldRow key={field.field_key} field={field}>
            <div className = "border rounded p-2 mb-2">
                <div className = "d-flex align-items-center gap-2 flex-wrap">
                <label children = {`Field ${index + 1}`} htmlFor = {String(field.field_key) + " label"}/>
                <input
                    id = {String(field.field_key) + " label"}
                    name = {String(field.field_key) + " label"}
                    required
                    className = "form-control inline"
                    placeholder = "Label"
                    value = {field.label}
                    onChange = {e =>
                        fb.updateField(field.field_key, { label: e.target.value })
                    }
                />
                <select
                    id = {String(field.field_key) + " field_type"}
                    name = {String(field.field_key) + " field_type"}
                    className = "form-control inline"
                    value = {field.field_type}
                    onChange = {e => {
                    const nextType = e.target.value as FieldType;
                    fb.updateField(field.field_key, {
                        default_value: null, 
                        field_type: nextType,
                        ...(["display"].includes(nextType) ? { is_required: 0 }: {}),
                        ...(["multiSelect", "checkbox",  "select", "radio"].includes(nextType) && field.options === null ? { options: [] } : {}),
                        ...(!["multiSelect", "checkbox", "select", "radio"].includes(nextType) && { options: null }),
                        ...(!["sub_form"].includes(nextType) && { sub_form_id: null, sub_form_name: null }),
                    });
                    }}
                >
                    {Object.entries(fieldType).map(([key, label]) => (
                    <option key = {key} value = {key}>
                        {label}
                    </option>
                    ))}
                </select>
                {field.field_type === "sub_form"?
                <AsyncFormSelect
                    // isMulti = {false}
                    placeholder = "Search sub form…"
                    // required = {!!field.is_required}
                    // name
                    // form_id
                    // inputId
                    fileName = "forms/searchForm.php"
                    value = {field.sub_form_id && field.sub_form_name ? {
                        value: String(field.sub_form_id),  
                        label: field.sub_form_name
                    } : null}
                    onChange = {(next) =>
                        next === null ? 
                        fb.updateField(field.field_key, {
                            sub_form_id: null,
                            sub_form_name: null
                        })
                        :
                        fb.updateField(field.field_key, {
                            sub_form_id: Number((next as SelectOption).value),
                            sub_form_name: (next as SelectOption).label
                        })
                    }
                />
                :null}
                {["user", "users"].includes(field.field_type) ?
                <AsyncFormSelect
                    isMulti = {field.field_type === "users"}
                    placeholder = "Search user…"
                    // required = {!!field.is_required}
                    // nam
                    // form_id
                    // inputId
                    fileName = "common/searchUser.php"
                    value = {field.default_value as SelectOption | SelectOption[] | null}
                    onChange = {e => fb.updateField(field.field_key, {
                        default_value: e as SelectOption | SelectOption[] | null
                    })}
                />
                :null}
                {["sub_form", "user", "users", "signature"].includes(field.field_type) && <div className = "d-flex align-items-center gap-1">
                    <input
                        id = {String(field.field_key) + " is_required"}
                        name = {String(field.field_key) + " is_required"}
                        className = "cssCheckBox"
                        type = "checkbox"
                        checked = {!!field.is_required}
                        onChange = {e =>
                            fb.updateField(field.field_key, {
                                is_required: e.target.checked ? 1 : 0
                            })
                        }
                    />
                    <label children = "Required" htmlFor = {String(field.field_key) + " is_required"}/>
                </div>}
                {["sub_form", "display", "signature", "user", "users"].includes(field.field_type) ? null : (
                    <>
                    {["date", "time", "month", "week", "datetime-local", "number", "range"].includes(field.field_type)?
                    <>
                    <label children = "Min" htmlFor = {String(field.field_key) + " min"}/>
                    <input
                        id = {String(field.field_key) + " min"}
                        name = {String(field.field_key) + " min"}
                        max = {field.max ?? ""}
                        type = {field.field_type === "range" ? "number" : field.field_type}
                        className = "form-control inline"
                        value = {field.min ?? ""}
                        onChange = {e => {
                            fb.updateField(field.field_key, {
                                min: e.target.value || null
                            })
                        }}
                    />
                    <label children = "Max" htmlFor = {String(field.field_key) + " max"}/>
                    <input
                        id = {String(field.field_key) + " max"}
                        name = {String(field.field_key) + " max"}
                        min = {field.min ?? ""}
                        type = {field.field_type === "range" ? "number" : field.field_type}
                        className = "form-control inline"
                        value = {field.max ?? ""}
                        onChange = {e => {
                            fb.updateField(field.field_key, {
                                max: e.target.value || null
                            })
                        }}
                    />
                    <label children = "Default" htmlFor = {String(field.field_key) + " default_value"}/>
                    <input
                        id = {String(field.field_key) + " default_value"}
                        name = {String(field.field_key) + " default_value"}
                        min = {field.min ?? ""}
                        max = {field.max ?? ""}
                        type = {field.field_type === "range" ? "number" : field.field_type}
                        className = "form-control inline"
                        value = {(field.default_value as string | null) ?? ""}
                        onChange = {e => {
                            fb.updateField(field.field_key, {
                                default_value: e.target.value || null
                            })
                        }}
                    />
                    {field.field_type === "range"?<span children = {(field.default_value as string | null) ?? "" } className = "ms-2"/>:null}
                    </>:
                    ["multiSelect", "checkbox"].includes(field.field_type)?
                    <Select
                        isMulti
                        inputId = {String(field.field_key)}
                        options = {field.options?.map(opt => ({label: opt, value: opt}))}
                        onChange = {e => fb.updateField(field.field_key, {
                            default_value: e.length ? e.map(x => x.value) : null
                        })}
                        value = {((field.default_value as string[] | null) ?? []).map((opt:string) => ({label: opt, value: opt}))}
                        classNamePrefix = "react-select"
                        closeMenuOnSelect = {true}
                    />:
                    ["select", "radio"].includes(field.field_type)?
                    <Select
                        isClearable
                        inputId = {String(field.field_key)}
                        options = {field.options?.map(opt => ({label: opt, value: opt}))}
                        onChange = {e => fb.updateField(field.field_key, {
                            default_value: e?.value || null
                        })}
                        value = {field.default_value ? { label: field.default_value, value: field.default_value } : null}
                        classNamePrefix = "react-select"
                        closeMenuOnSelect = {true}
                    />:
                    <input
                        name = {String(field.field_key) + " default_value"}
                        id  = {String(field.field_key) + " default_value"}
                        className = "form-control inline"
                        placeholder = "Default value"
                        value = {(field.default_value as string | null) ?? ""}
                        onChange = {e => {
                            fb.updateField(field.field_key, {
                                default_value: e.target.value || null
                            })
                        }}
                    />
                    }
                    {["text", "search", "url", "tel", "email", "password", "number"].includes(field.field_type)?<>
                    <label children = "Placeholder" htmlFor = {String(field.field_key) + " placeholder"}/>
                    <input
                        id = {String(field.field_key) + " placeholder"}
                        name = {String(field.field_key) + " placeholder"}
                        type = "text"
                        className = "form-control inline"
                        value = {(field.placeholder as string | null) ?? ""}
                        onChange = {e => {
                            fb.updateField(field.field_key, {
                                placeholder: e.target.value || null
                            })
                        }}
                    />
                    </>:null}
                    <div className = "d-flex align-items-center gap-1">
                        <input
                            name = {String(field.field_key) + " is_required"}
                            id  = {String(field.field_key) + " is_required"}
                            className = "cssCheckBox"
                            type = "checkbox"
                            checked = {!!field.is_required}
                            onChange = {e =>
                                fb.updateField(field.field_key, {
                                    is_required: e.target.checked ? 1 : 0
                                })
                            }
                        />
                        <label children = "Required" htmlFor = {String(field.field_key) + " is_required"}/>
                    </div>
                    </>
                )}
                <button
                    type = "button"
                    className = "btn btn-danger ms-auto"
                    onClick = {() => fb.removeField(field.field_key)}
                >
                    ✕
                </button>
                </div>
                {["multiSelect", "checkbox", "select", "radio"].includes(field.field_type) && (
                <SortableCreatableOptionsEditor
                    field = {field}
                    onChange = {(patch: Partial<field>) =>
                        fb.updateField(field.field_key, patch)
                    }
                />
                )}
            </div>
            </SortableFieldRow>
        ))}
        </SortableContext>
    </DndContext>
    <button
        children = " + Add Field"
        type = "button"
        className = "btn btn-primary mb-3"
        onClick = {() => fb.addField("text")}
    />
    <hr/>
    <h5>Rules</h5>
    {fb.rules.map(rule => (
        <RuleEditor
            key = {rule.id}
            rule = {rule}
            fields = {fb.fields}
            onChange = {patch =>
                fb.updateRule(rule.id, patch)
            }
            onRemove = {() => fb.removeRule(rule.id)}
        />
    ))}
    <button
        type = "button"
        className = "btn btn-primary mb-3"
        onClick = {() => fb.addRule()}
    >
        + Add Rule
    </button>
    <div>
        <button
            children = "Save Draft"
            type = "button"
            className = "btn btn-success me-3"
            onClick = {() => fb.submitForm(false)}
        />
        <button
            children = "Publish"
            type = "button"
            className = "btn btn-primary me-3"
            onClick = {() => fb.submitForm(true)}
        />
        <Link
            children = "Back"
            to = {fb.isEdit ? ".." : "/Forms/Setting"}
            className = "btn btn-outline-secondary float-end"
        />
    </div>
    </form>
  );
}
